import { pick } from 'lodash';
import { stringToLuxon } from '@/helpers';
import api from './api';

import type { ActionContext } from 'vuex';
import type { State } from '@/typings/vuex';
import type { Training as StudentTraining } from '@/views/LMS/Users/Show/Trainings/index.d';
import type {
  State as TrainingState,
  FetchExistingTrainingNamesPayload,
  UpdateTrainingParticipantsPayload,
  ExistingTrainingName,
  TrainingTemplate,
  Training,
} from './vuex.d';

export function transformTrainingTemplateDates(template: TrainingTemplate<string>): TrainingTemplate {
  const { certificateReminderDate, certificateExpiryDate } = template.settings;

  return {
    ...template,
    settings: {
      ...template.settings,
      certificateReminderDate: certificateReminderDate ? stringToLuxon(certificateReminderDate) : undefined,
      certificateExpiryDate: certificateExpiryDate ? stringToLuxon(certificateExpiryDate) : undefined,
    },
  };
}

export default {
  async fetchTraining(
    context: ActionContext<TrainingState, State>,
    trainingId: number,
  ): Promise<Training> {
    const training = await api.fetchTraining(trainingId);

    context.commit('setTraining', training);

    return training;
  },

  async fetchExistingTrainingNames(
    context: ActionContext<TrainingState, State>,
    payload?: FetchExistingTrainingNamesPayload,
  ): Promise<ExistingTrainingName[]> {
    return api.fetchExistingTrainingNames(payload?.searchString, payload?.forceFresh);
  },

  async fetchTrainingTemplates(): Promise<TrainingTemplate[]> {
    const templates = await api.fetchTrainingTemplates();

    return templates.map(transformTrainingTemplateDates);
  },

  async removeTrainingTemplate(
    context: ActionContext<TrainingState, State>,
    templateId: number,
  ): Promise<void> {
    return api.removeTrainingTemplate(templateId);
  },

  async getTrainings(
    context: ActionContext<TrainingState, State>,
    userId: number,
  ): Promise<StudentTraining[]> {
    const { searchString } = context.state;
    const trainings = await api.fetchTrainings(userId, searchString);

    context.commit('setTrainings', trainings);

    return trainings;
  },

  resetSearchAndFilter(context: ActionContext<TrainingState, State>): void {
    context.commit('setSearchString', '');
    context.commit('setFilter', 'showAll');
  },

  async updateTrainingParticipants(
    context: ActionContext<TrainingState, State>,
    payload: UpdateTrainingParticipantsPayload,
  ): Promise<void> {
    const trainingId = payload.trainingId ?? context.state.training?.id;

    if (typeof trainingId === 'number') {
      const participants = pick(payload, 'companyUserIds', 'groupIds');
      const training = await api.updateTrainingParticipants(trainingId, participants);

      context.commit('setTrainingParticipants', training);
    }
  },
};
